import React, { useEffect, useRef, useState } from "react";
import Select, {
  MultiValue,
  SingleValue,
  components,
  OptionProps,
  StylesConfig,
} from "react-select";
import { Label } from "reactstrap";
import { OptionType } from "../../utils/Types";

//Dark theme styles with adaptive height
const baseSelectStyles: StylesConfig<OptionType, boolean> = {
  control: (provided, state) => {
    const sel = state.selectProps.value;
    const count = Array.isArray(sel) ? sel.length : sel ? 1 : 0;
    const expand = count >= 3;

    return {
      ...provided,
      border: "none",
      borderBottom: "1px solid #6f6f6f",
      backgroundColor: "#2b2b2b",
      minHeight: 38,
      height: expand ? "auto" : 38,
      borderRadius: 4,
      color: "#ffffff",
      cursor: "pointer",
      boxShadow: "none",
      display: "flex",
      alignItems: "center",
      "&:hover": {
        borderBottom: "1px solid #6f6f6f",
      },
    };
  },
  valueContainer: (provided, state) => {
    const sel = state.selectProps.value;
    const count = Array.isArray(sel) ? sel.length : sel ? 1 : 0;
    const expand = count >= 3;

    return {
      ...provided,
      padding: "4px 8px",
      display: "flex",
      alignItems: "center",
      flexWrap: expand ? "wrap" : "nowrap",
      overflow: "hidden",
    };
  },
  placeholder: (provided) => ({
    ...provided,
    color: "#ffffff",
    fontSize: "0.95rem",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#ffffff",
    fontSize: "1rem",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#2b2b2b",
    border: "1px solid #6f6f6f",
    boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#3a3a3a" : "#2b2b2b",
    color: "#eaeaea",
    padding: "10px 12px",
    cursor: "pointer",
  }),
  input: (provided) => ({ ...provided, color: "#fff" }),
  indicatorsContainer: (provided) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    gap: 4,
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#cfcfcf",
    padding: 6,
    ":hover": { color: "#8bb8ff" },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: "#cfcfcf",
    padding: 6,
    ":hover": { color: "#ff9090" },
  }),
  menuList: (provided) => ({ ...provided, padding: 0 }),
};

//  Checkbox or Radio option component
const CheckboxOrRadioOption = (
  props: OptionProps<OptionType, boolean> & { isMulti: boolean }
) => {
  const { isSelected, label, isMulti } = props;
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <input
          type={isMulti ? "checkbox" : "radio"}
          checked={isSelected}
          readOnly
          style={{ accentColor: "#3f7cff" }}
        />
        <span>{label}</span>
      </div>
    </components.Option>
  );
};


const CustomValueContainer = (props: any) => {
  const selected = props.getValue();
  let displayText = props.children;
  if (selected.length > 0) {
    const firstLabel = selected[0].label;
    const moreCount = selected.length - 1;
    displayText =
      moreCount > 0 ? `${firstLabel} +${moreCount}` : firstLabel;
  }
  return (
    <components.ValueContainer {...props}>
      <span style={{ color: "#fff", fontSize: "0.95rem" }}>{displayText}</span>
    </components.ValueContainer>
  );
};

interface SeasonSelectProps {
  label: string;
  options: OptionType[];
  value: OptionType[]; 
  onChange: (value: OptionType[]) => void;
  isMulti: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
}

const SeasonMultiSelect: React.FC<SeasonSelectProps> = ({
  label,
  options,
  value,
  onChange,
  isMulti,
  isDisabled,
  isRequired,
}) => {
  // react-select expects: array for multi, single object or null for single
  const selectedValue = isMulti ? value : value[0] || null;

  //  Control menu open state to close on outside click
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuIsOpen &&
        containerRef.current &&
        !containerRef.current.contains(target)
      ) {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [menuIsOpen]);

  return (
    <div ref={containerRef}>
      {label && (
        <Label className="custom-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </Label>
      )}

      <Select
        classNamePrefix="react-select"
        options={options}
        value={selectedValue}
        onChange={(newValue) => {
          if (isMulti) {
            const mv = newValue as MultiValue<OptionType>;
            onChange(mv.map((v) => v));
          } else {
            const sv = newValue as SingleValue<OptionType>;
            onChange(sv ? [sv] : []);
          }
        }}
        isMulti={isMulti}
        isClearable
        isSearchable={false}
        placeholder={isMulti ? "Select Season(s)" : "Select Season"}
        styles={baseSelectStyles}
        isDisabled={isDisabled}
        aria-label="Season"
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        //Control open/close + close on outside click
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        // Optional: also close on blur/tab-out for good UX
        onBlur={() => setMenuIsOpen(false)}
        components={{
          Option: (props) => (
            <CheckboxOrRadioOption {...props} isMulti={isMulti} />
          ),
          IndicatorSeparator: () => null,
          // Only use custom ValueContainer for multi-select
          ValueContainer: isMulti
            ? CustomValueContainer
            : components.ValueContainer,
          //  Only hide chips for multi-select
          ...(isMulti
            ? {
                MultiValueContainer: () => null,
                MultiValueLabel: () => null,
                MultiValueRemove: () => null,
              }
            : {}),
        }}
      />
    </div>
  );
};

export default SeasonMultiSelect;
